// src/pages/Dashboard/components/ManagerMorningCard.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. A manager lands on Today, not on /manager/, and Today had
//               nothing for them unless somebody was waiting on a sign off.
//
// THE FLOOR THIS MORNING, IN THREE LINES, AND A DOOR.
//
// The whole panel lives at the top of /manager/ (FloorThisMorning.jsx). This
// is the part of it a manager wants in the first five seconds after signing
// in: how many warmed up, who nobody has noticed this week, and whether the
// morning meeting is ready. Then one tap to the morning itself.
//
// Same numbers as the panel, computed by the same helpers (marksMath.js), so
// Today and the manager page cannot disagree. A failed read drops its line
// rather than showing a zero, and if every read failed the card does not
// render at all.

import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Icon } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowRight, Sun } from 'lucide-react'
import { listFloor } from '../../../lib/oneOnOne'
import { getMeeting, meetingState } from '../../../lib/morningMeeting'
import { listSince, listMorningEvents, morningFor, floorLine, dueForRecognition, joinNames } from '../../../lib/recognitions'
import { firstNameFor } from '../../../lib/names.js'
import { dayKey, addDays, parseDay } from '../../../lib/localDate'

const MEETING_LINE = {
  held: 'The morning meeting has been held.',
  prepared: 'The morning meeting is prepared.',
  unprepared: 'The meeting was held without an agenda.',
  empty: 'The morning meeting is not prepared yet.',
  none: 'The morning meeting is not prepared yet.',
}

export default function ManagerMorningCard({ dealershipId }) {
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!dealershipId) return
    let live = true
    const today = dayKey()
    ;(async () => {
      const [f, m, r] = await Promise.all([
        listFloor(dealershipId),
        getMeeting(dealershipId, today),
        listSince(dealershipId, parseDay(addDays(today, -7)).toISOString()),
      ])
      if (!live || f.error) return
      const floor = f.data
      const e = await listMorningEvents(floor.map((p) => p.id), parseDay(today).toISOString())
      if (!live) return
      setData({
        floor,
        meeting: m.error ? undefined : meetingState(m.data),
        morning: e.data ? morningFor(floor, e.data, today) : null,
        due: r.data ? dueForRecognition(floor, r.data) : null,
      })
    })()
    return () => {
      live = false
    }
  }, [dealershipId])

  if (!data || data.floor.length === 0) return null

  const names = (data.due || []).map((p) => firstNameFor(p) || 'Somebody')
  const dueLine =
    data.due == null
      ? null
      : data.due.length === 0
        ? 'Everybody on the floor has been recognised this week.'
        : data.due.length === data.floor.length
          ? 'Nobody on the floor has been recognised this week.'
          : `Not recognised this week: ${joinNames(names)}.`

  return (
    <Box
      as={RouterLink}
      to="/manager/"
      display="block"
      bg="card"
      border="1px solid"
      borderColor="line"
      borderRadius="card"
      px={{ base: 5, md: 6 }}
      py={5}
      role="group"
      _hover={{ textDecoration: 'none', borderColor: 'lineStrong' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
    >
      <HStack spacing={2} mb={2} color="inkDim">
        <Sun size={14} aria-hidden="true" />
        <Text textStyle="eyebrow">THE FLOOR THIS MORNING</Text>
      </HStack>
      <VStack align="stretch" spacing={1}>
        {data.morning && (
          <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em">
            {floorLine(data.morning)}
          </Text>
        )}
        {dueLine && (
          <Text fontSize="body-sm" color="inkMuted">
            {dueLine}
          </Text>
        )}
        {data.meeting && (
          <Text fontSize="body-sm" color="inkMuted">
            {MEETING_LINE[data.meeting]}
          </Text>
        )}
      </VStack>
      <HStack spacing={1.5} mt={3} fontSize="sm" fontWeight={600}>
        <Text>Open your morning</Text>
        <Icon as={ArrowRight} boxSize={4} transition="transform 140ms ease" _groupHover={{ transform: 'translateX(3px)' }} />
      </HStack>
    </Box>
  )
}
